import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { usePublic } from "@/pages/public/PublicLayout";
import api from "@/lib/api";
import { ArrowLeft, Star, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const T = {
  it: { back: "Torna al menù", title: "Lascia una recensione", welcome: (n) => `Il tuo parere è importante per noi. Aiutaci a migliorare condividendo la tua esperienza da ${n}.`, food: "Soddisfazione sui piatti", service: "Valutazione del servizio", recommend: "Ci consiglieresti ad amici e conoscenti?", email: "La tua email", emailPh: "nome@email.com", comment: "Commento (facoltativo)", commentPh: "Raccontaci la tua esperienza…", submit: "Invia recensione", sending: "Invio…", thanksTitle: "Grazie!", thanksBody: "La tua recensione è stata inviata. Grazie per averci aiutato a migliorare.", again: "Torna al menù", errRatings: "Assegna una valutazione a tutte le voci", errEmail: "Inserisci un'email valida" },
  en: { back: "Back to menu", title: "Leave a review", welcome: (n) => `Your opinion matters to us. Help us improve by sharing your experience at ${n}.`, food: "Satisfaction with the dishes", service: "Service rating", recommend: "Would you recommend us to friends?", email: "Your email", emailPh: "name@email.com", comment: "Comment (optional)", commentPh: "Tell us about your experience…", submit: "Send review", sending: "Sending…", thanksTitle: "Thank you!", thanksBody: "Your review has been sent. Thanks for helping us improve.", again: "Back to menu", errRatings: "Please rate all items", errEmail: "Enter a valid email" },
  fr: { back: "Retour au menu", title: "Laisser un avis", welcome: (n) => `Votre avis compte pour nous. Aidez-nous à nous améliorer en partageant votre expérience chez ${n}.`, food: "Satisfaction sur les plats", service: "Évaluation du service", recommend: "Nous recommanderiez-vous à des amis ?", email: "Votre e-mail", emailPh: "nom@email.com", comment: "Commentaire (facultatif)", commentPh: "Parlez-nous de votre expérience…", submit: "Envoyer l'avis", sending: "Envoi…", thanksTitle: "Merci !", thanksBody: "Votre avis a été envoyé. Merci de nous aider à nous améliorer.", again: "Retour au menu", errRatings: "Veuillez évaluer tous les éléments", errEmail: "Saisissez un e-mail valide" },
  de: { back: "Zurück zum Menü", title: "Bewertung abgeben", welcome: (n) => `Ihre Meinung ist uns wichtig. Helfen Sie uns, besser zu werden, indem Sie Ihre Erfahrung bei ${n} teilen.`, food: "Zufriedenheit mit den Gerichten", service: "Bewertung des Service", recommend: "Würden Sie uns Freunden empfehlen?", email: "Ihre E-Mail", emailPh: "name@email.com", comment: "Kommentar (optional)", commentPh: "Erzählen Sie uns von Ihrer Erfahrung…", submit: "Bewertung senden", sending: "Senden…", thanksTitle: "Danke!", thanksBody: "Ihre Bewertung wurde gesendet. Danke, dass Sie uns helfen, besser zu werden.", again: "Zurück zum Menü", errRatings: "Bitte bewerten Sie alle Punkte", errEmail: "Gültige E-Mail eingeben" },
  es: { back: "Volver al menú", title: "Deja una reseña", welcome: (n) => `Tu opinión es importante para nosotros. Ayúdanos a mejorar compartiendo tu experiencia en ${n}.`, food: "Satisfacción con los platos", service: "Valoración del servicio", recommend: "¿Nos recomendarías a tus amigos?", email: "Tu email", emailPh: "nombre@email.com", comment: "Comentario (opcional)", commentPh: "Cuéntanos tu experiencia…", submit: "Enviar reseña", sending: "Enviando…", thanksTitle: "¡Gracias!", thanksBody: "Tu reseña ha sido enviada. Gracias por ayudarnos a mejorar.", again: "Volver al menú", errRatings: "Valora todos los apartados", errEmail: "Introduce un email válido" },
  pt: { back: "Voltar ao menu", title: "Deixe uma avaliação", welcome: (n) => `A sua opinião é importante para nós. Ajude-nos a melhorar partilhando a sua experiência em ${n}.`, food: "Satisfação com os pratos", service: "Avaliação do serviço", recommend: "Recomendaria a amigos?", email: "O seu email", emailPh: "nome@email.com", comment: "Comentário (opcional)", commentPh: "Conte-nos a sua experiência…", submit: "Enviar avaliação", sending: "A enviar…", thanksTitle: "Obrigado!", thanksBody: "A sua avaliação foi enviada. Obrigado por nos ajudar a melhorar.", again: "Voltar ao menu", errRatings: "Avalie todos os itens", errEmail: "Introduza um email válido" },
};

const StarRow = ({ label, value, onChange, theme, testid }) => {
  const [hover, setHover] = useState(0);
  return (
    <div data-testid={testid}>
      <p className="text-sm mb-2" style={{ color: theme.text }}>{label}</p>
      <div className="flex gap-1.5" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = n <= (hover || value);
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              onMouseEnter={() => setHover(n)}
              data-testid={`${testid}-star-${n}`}
              aria-label={`${n}`}
              className="transition-transform hover:scale-110 active:scale-95"
            >
              <Star className="w-8 h-8" style={{ color: active ? theme.accent : theme.muted + "66", fill: active ? theme.accent : "transparent" }} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Reviews = () => {
  const { data, theme, lang, slug } = usePublic();
  const navigate = useNavigate();
  const t = T[lang] || T.it;
  const [ratings, setRatings] = useState({ food: 0, service: 0, recommend: 0 });
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [focused, setFocused] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const fieldStyle = (name) => ({
    backgroundColor: theme.surface,
    color: theme.text,
    border: `1px solid ${theme.accent}${focused === name ? "" : "33"}`,
    boxShadow: focused === name ? `0 0 0 3px ${theme.accent}33` : "none",
    outline: "none",
    transition: "box-shadow 0.2s, border-color 0.2s",
  });

  const submit = async () => {
    if (!ratings.food || !ratings.service || !ratings.recommend) return toast.error(t.errRatings);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return toast.error(t.errEmail);
    setSending(true);
    try {
      await api.post(`/public/${slug}/review`, {
        food_rating: ratings.food, service_rating: ratings.service, recommend_rating: ratings.recommend,
        email: email.trim(), comment: comment.trim(),
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'invio");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="pt-16 text-center" data-testid="review-thanks">
        <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5" style={{ backgroundColor: theme.accent + "22", color: theme.accent }}>
          <Check className="w-8 h-8" />
        </div>
        <h2 className="text-3xl mb-3" style={{ fontFamily: theme.heading, color: theme.text }}>{t.thanksTitle}</h2>
        <p className="text-sm max-w-sm mx-auto leading-relaxed" style={{ color: theme.muted }}>{t.thanksBody}</p>
        <button onClick={() => navigate(`/m/${slug}`)} data-testid="review-back-thanks" className="mt-8 rounded-full px-6 py-2.5 text-sm font-semibold transition-transform active:scale-95" style={{ backgroundColor: theme.accent, color: theme.bg }}>{t.again}</button>
      </motion.div>
    );
  }

  return (
    <div className="pt-4">
      <button onClick={() => navigate(`/m/${slug}`)} data-testid="review-back" className="flex items-center gap-1.5 text-sm mb-5" style={{ color: theme.muted }}>
        <ArrowLeft className="w-4 h-4" /> {t.back}
      </button>
      <h2 className="text-3xl mb-2" style={{ fontFamily: theme.heading, color: theme.text }}>{t.title}</h2>
      <p className="text-sm mb-8 leading-relaxed" style={{ color: theme.muted }}>{t.welcome(data.restaurant?.name || "")}</p>

      <div className="space-y-6">
        <StarRow label={t.food} value={ratings.food} onChange={(v) => setRatings((r) => ({ ...r, food: v }))} theme={theme} testid="rating-food" />
        <StarRow label={t.service} value={ratings.service} onChange={(v) => setRatings((r) => ({ ...r, service: v }))} theme={theme} testid="rating-service" />
        <StarRow label={t.recommend} value={ratings.recommend} onChange={(v) => setRatings((r) => ({ ...r, recommend: v }))} theme={theme} testid="rating-recommend" />

        <div>
          <p className="text-sm mb-2" style={{ color: theme.text }}>{t.email} <span style={{ color: theme.accent }}>*</span></p>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocused("email")} onBlur={() => setFocused(null)}
            placeholder={t.emailPh} data-testid="review-email"
            className="w-full rounded-xl px-4 py-3 text-sm placeholder:opacity-40"
            style={fieldStyle("email")}
          />
        </div>

        <div>
          <p className="text-sm mb-2" style={{ color: theme.text }}>{t.comment}</p>
          <textarea
            value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
            onFocus={() => setFocused("comment")} onBlur={() => setFocused(null)}
            placeholder={t.commentPh} data-testid="review-comment"
            className="w-full rounded-xl px-4 py-3 text-sm placeholder:opacity-40 resize-none"
            style={fieldStyle("comment")}
          />
        </div>

        <button
          onClick={submit} disabled={sending} data-testid="review-submit"
          className="w-full rounded-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:opacity-60"
          style={{ backgroundColor: theme.accent, color: theme.bg }}
        >
          {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.sending}</> : t.submit}
        </button>
      </div>
    </div>
  );
};

export default Reviews;
